import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Button, Card, Spinner, Alert, AlertDescription,
} from '@agion/ui'
import { ArrowLeft, HardDrive } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { formatDate } from '@/core/infra_utils'
import { getImage, type PackerImage } from './infra_image.service'
import { ImageStatusBadge } from './InfraImageStatusBadge'

export function ImageDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [image, setImage] = useState<PackerImage | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    setIsLoading(true)
    void getImage(id)
      .then(setImage)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load image'))
      .finally(() => setIsLoading(false))
  }, [id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 space-y-4">
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/images')}>
          <ArrowLeft size={14} className="mr-1" />
          {t('pages:images.backToList', 'Back to Images')}
        </Button>
      </div>
    )
  }

  if (!image) {
    return (
      <div className="p-6 space-y-4">
        <Alert variant="warning">
          <AlertDescription>{t('pages:images.notFound', 'Image not found')}</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/images')}>
          <ArrowLeft size={14} className="mr-1" />
          {t('pages:images.backToList', 'Back to Images')}
        </Button>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={image.name}
        description={`${image.cloudProvider} / ${image.region}`}
        icon={HardDrive}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/images')}>
            <ArrowLeft size={14} className="mr-1" />
            {t('pages:images.backToList', 'Back to Images')}
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">{t('pages:images.detail.info', 'Image Information')}</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('pages:images.detail.status', 'Status')}</dt>
              <dd><ImageStatusBadge status={image.status} /></dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('pages:images.detail.version', 'Version')}</dt>
              <dd className="font-mono">{image.version}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('pages:images.detail.template', 'Template')}</dt>
              <dd>{image.templateId}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('pages:images.detail.provider', 'Cloud Provider')}</dt>
              <dd>{image.cloudProvider}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('pages:images.detail.region', 'Region')}</dt>
              <dd>{image.region}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('pages:images.detail.imageId', 'Cloud Image ID')}</dt>
              <dd className="font-mono text-sm truncate max-w-[200px]">{image.imageId || '-'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('pages:images.detail.os', 'OS')}</dt>
              <dd>{image.osVersion}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('pages:images.detail.created', 'Created')}</dt>
              <dd>{formatDate(image.createdAt)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t('pages:images.detail.updated', 'Updated')}</dt>
              <dd>{formatDate(image.updatedAt)}</dd>
            </div>
          </dl>
        </Card>

        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">{t('pages:images.detail.binaries', 'Binary Versions')}</h3>
          {Object.keys(image.binaryVersions).length > 0 ? (
            <dl className="space-y-2">
              {Object.entries(image.binaryVersions).map(([name, version]) => (
                <div key={name} className="flex justify-between">
                  <dt className="text-slate-500 font-mono text-sm">{name}</dt>
                  <dd className="font-mono text-sm">{version}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-slate-400 text-sm">
              {t('pages:images.detail.noBinaries', 'No binary version information available.')}
            </p>
          )}

          {image.buildOperationId && (
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/operations/${image.buildOperationId}`)}
              >
                {t('pages:images.detail.viewOperation', 'View Build Operation')}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
